export const UNIT_SETTING_KEYS = Object.freeze([
  'country_code', 'operating_currency', 'timezone', 'locale', 'default_tax_rate',
]);
export function unitSettingsForm(unit = {}) {
  return Object.fromEntries(UNIT_SETTING_KEYS.map(key => [key, unit[key] == null ? '' : String(unit[key])]));
}
function normalize(key, value) {
  const text = String(value ?? '').trim();
  if (!text) return null;
  if (key === 'country_code' || key === 'operating_currency') return text.toUpperCase();
  if (key === 'default_tax_rate') {
    if (!/^\d+(?:\.\d+)?$/.test(text)) throw new Error('Enter a default tax rate between 0 and 100.');
    return Number(text);
  }
  return text;
}
function validate(key, value) {
  if (value == null) return;
  if (key === 'country_code' && !/^[A-Z]{2}$/.test(value)) throw new Error('Enter a two-letter country code, such as FR.');
  if (key === 'operating_currency' && !/^[A-Z]{3}$/.test(value)) throw new Error('Select a three-letter operating currency.');
  if (key === 'default_tax_rate' && (!Number.isFinite(value) || value < 0 || value > 100)) {
    throw new Error('Enter a default tax rate between 0 and 100.');
  }
  if (key === 'timezone') {
    try { new Intl.DateTimeFormat('en-GB', { timeZone: value }); }
    catch { throw new Error('Enter a valid timezone, such as Europe/Paris.'); }
  }
  if (key === 'locale') {
    try {
      if (Intl.NumberFormat.supportedLocalesOf([value]).length !== 1) throw new Error();
    } catch { throw new Error('Enter a supported locale, such as fr-FR.'); }
  }
}

// Existing fields that the GET endpoint omitted are not silently cleared on edit.
export function unitSettingsPatch(form, original = {}, { creating = false } = {}) {
  const patch = {};
  for (const key of UNIT_SETTING_KEYS) {
    const raw = String(form[key] ?? '').trim();
    const before = String(original[key] ?? '').trim();
    if (!creating && raw === before) continue;
    const value = normalize(key, raw);
    validate(key, value);
    if (['operating_currency', 'timezone', 'locale'].includes(key) && value == null) {
      throw new Error('Set the unit operating currency, timezone and locale.');
    }
    if (creating && value == null) continue;
    let previous;
    try { previous = normalize(key, before); } catch { previous = undefined; }
    if (creating || value !== previous) patch[key] = value;
  }
  return patch;
}

function comparable(key, value) {
  const normalized = normalize(key, value);
  if (normalized == null) return null;
  if (key === 'timezone') return new Intl.DateTimeFormat('en-GB', { timeZone: normalized }).resolvedOptions().timeZone;
  if (key === 'locale') return Intl.getCanonicalLocales(normalized)[0];
  return normalized;
}
export function verifyUnitSettingsReadback(unit, companyId, unitId, requested) {
  if (!unit || String(unit.id) !== String(unitId) || String(unit.company_id) !== String(companyId)) {
    throw new Error('The saved unit could not be verified. Refresh the unit list before trying again.');
  }
  for (const key of UNIT_SETTING_KEYS.filter(key => Object.hasOwn(requested, key))) {
    if (!Object.hasOwn(unit, key) || comparable(key, unit[key]) !== comparable(key, requested[key])) {
      throw new Error('The unit settings were not confirmed after saving. Refresh the unit list before trying again.');
    }
  }
  return unit;
}
